// @flow

import normalize from 'json-api-normalizer';

import type { SessionAttributesType, SessionType } from 'types/sessions';

import ApiError from 'utils/errors/api-error';
import InternalError from 'utils/errors/internal-error';
import userAuth from 'utils/user-auth';

import calculateRequestPath from './calculate-resource-request-path';

const API_BASE_URL = '/api/payments_web/v1';
const DEFAULT_HEADERS = { 'Content-Type': 'application/json' };

export type ResponseType = {
  status: number,
};

const checkStatus = (response: ResponseType) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  if (response.status === 401) {
    userAuth.clear();
  }

  throw new ApiError(response);
};

const makeRequest = async (endpoint: string, optOverrides = {}) => {
  const opts = {
    credentials: 'same-origin',
    method: 'GET',
    headers: DEFAULT_HEADERS,
    ...optOverrides,
  };

  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, opts);
    const json = response.status === 204 ? {} : await response.json();
    return { status: response.status, ...normalize(json, { endpoint, filterEndpoint: false }) };
  } catch (error) {
    throw new InternalError(error);
  }
};

export const post = (data: mixed, path: string) =>
  makeRequest(path, { method: 'POST', body: JSON.stringify(data) });

export const put = (data: mixed, path: string) =>
  makeRequest(path, { method: 'PUT', body: JSON.stringify(data) });

export const get = (path: string) => makeRequest(path, { method: 'GET' });

// Tutor Authentication
const postTutorSignIn = (data: mixed) => post(data, 'tutors/login');
const postTutorResetPasswordEmail = (data: mixed) => post(data, '/tutors/reset_password_init');
const postTutorPassword = (data: mixed) => post(data, '/tutors/reset_password');

// Session
const postSessionCreate = (data: {
  data: {
    type: 'sessions',
    attributes: $Shape<{ ...SessionAttributesType, sessionRecurrenceCount: ?number }>,
  },
}): Promise<any> => post(data, 'tutors/sessions');

const putSession = (
  sessionId: string,
  data: {
    data: $Shape<{
      ...SessionType,
      attributes: $Shape<{ ...SessionAttributesType, shouldUpdateFutureSessions: boolean }>,
    }>,
  },
): Promise<any> => put(data, `tutors/sessions/${sessionId}`);

const subscribeToCalendar = () => post({}, 'tutors/google-calendar-subscriptions/subscribe');
const unsubscribeFromCalendar = () => post({}, 'tutors/google-calendar-subscriptions/unsubscribe');

// payer
const getPayer = () => get(`payers/me`);

const getPayerAuthenticated = (token: string) =>
  get(`payers/authenticate?web_public_token=${token}&include=tutor,client`);

const getNewPayerAuthenticated = (payerId: string) =>
  get(`payers/new/authenticate?payer_id=${payerId}`);

const postPayerPaymentMethod = (data: mixed) => post(data, 'payers/payment_methods');
const postPayerChargeConfirmation = (chargeId: string) =>
  post({}, `payers/charges/${chargeId}/confirm`);

export {
  calculateRequestPath,
  checkStatus,
  postTutorSignIn,
  postTutorResetPasswordEmail,
  postTutorPassword,
  getPayerAuthenticated,
  getNewPayerAuthenticated,
  postPayerPaymentMethod,
  postPayerChargeConfirmation,
  getPayer,
  postSessionCreate,
  putSession,
  subscribeToCalendar,
  unsubscribeFromCalendar,
};



// WEBPACK FOOTER //
// ./src/api/index.js